import React from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { TYPE_COLORS, TYPE_NAMES_RU, getDeviceStatus } from './equipmentTypes';
import type { RackEquipmentItem } from './rackTypes';

// ─────────────────────────────────────────────────────────────────────────────
// ZeroUEquipment — PDU/UPS in vertical side columns (0U mounting)
// ─────────────────────────────────────────────────────────────────────────────

interface ZeroUEquipmentProps {
  eq: RackEquipmentItem;
  isMountingMode?: boolean;
  onClick?: () => void;
  isDark?: boolean;
  pduSummary?: { free_outlets: number; total_outlets: number } | null;
}

export function ZeroUEquipment({ eq, isMountingMode, onClick, isDark = true, pduSummary }: ZeroUEquipmentProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: eq.id,
    data: { equipment: eq },
    disabled: !isMountingMode,
  });

  const style: React.CSSProperties = {
    zIndex:    isDragging ? 50 : 10,
    opacity:   isDragging ? 0.8 : 1,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    height:    '90px',
    width:     '100%',
  };

  const bgColor = TYPE_COLORS[eq.equipment_type] || TYPE_COLORS.unknown;
  const status  = getDeviceStatus(eq.status);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(isMountingMode ? listeners : {})}
      onClick={onClick}
      className={`relative rounded border p-1 flex flex-col justify-between font-mono cursor-pointer transition-all ${
        isDark ? 'border-slate-700/80' : 'border-gray-300/80'
      } ${
        isDragging ? 'shadow-[0_0_15px_rgba(0,210,255,0.6)] border-cyan-400' : ''
      }`}
    >
      {/* Color overlay */}
      <div
        className="absolute inset-0 rounded pointer-events-none"
        style={{ backgroundColor: bgColor, opacity: isDark ? 0.20 : 0.30 }}
      />

      {/* Type header */}
      <div className={`flex justify-between items-center text-[8px] font-bold z-10 ${
        isDark ? 'text-slate-300' : 'text-slate-700'
      }`}>
        <span>0U</span>
        <span className="uppercase text-[7px] opacity-75">{TYPE_NAMES_RU[eq.equipment_type] || eq.equipment_type}</span>
      </div>

      {/* Name */}
      <div className={`text-[9px] font-semibold truncate z-10 my-auto ${
        isDark ? 'text-slate-100' : 'text-slate-800'
      }`}>
        {eq.name}
      </div>

      {/* Status dot */}
      <div className="absolute top-1 right-1 flex gap-0.5">
        <span className={`w-1.5 h-1.5 rounded-full ${
          status === 'critical' ? 'bg-red-500 animate-ping' :
          status === 'warning'  ? 'bg-amber-500' : 'bg-emerald-400'
        }`} />
      </div>

      {/* PDU outlet badge */}
      {pduSummary && (
        <div className="z-10 mt-auto pt-1 flex justify-between items-center text-[8px] font-mono">
          <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Розетки:</span>
          <span
            aria-label={`PDU: ${pduSummary.free_outlets} из ${pduSummary.total_outlets} свободно`}
            className={`font-bold px-1 rounded-sm tabular-nums ${
              pduSummary.free_outlets === 0
                ? 'bg-red-950/80 text-red-300'
                : pduSummary.free_outlets <= 2
                ? 'bg-amber-950/80 text-amber-300'
                : 'bg-emerald-950/80 text-emerald-300'
            }`}
          >
            {pduSummary.free_outlets}/{pduSummary.total_outlets}
          </span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ZeroUDroppableColumn — droppable container for 0U equipment side column
// ─────────────────────────────────────────────────────────────────────────────

interface ZeroUDroppableColumnProps {
  mount: string;
  children: React.ReactNode;
}

export function ZeroUDroppableColumn({ mount, children }: ZeroUDroppableColumnProps) {
  const { setNodeRef } = useDroppable({
    id: `zero-u-${mount}`,
    data: { isZeroU: true, mount },
  });

  return (
    <div ref={setNodeRef} className="flex-1 p-1 space-y-1 overflow-y-auto">
      {children}
    </div>
  );
}
